/**
 * General settings (ARCHITECTURE.md §8, §0.5, §11).
 *
 * Three concerns:
 *   1. Operational defaults — timezone, base currency, default dry-run.
 *   2. The **global `trading_armed` master switch**. Arming is the single most
 *      consequential action in the product, so it is guarded with an explicit
 *      acknowledgement dialog; the server additionally requires a *verified* Toss
 *      credential and `confirm: true`, so "clone, deploy, accidentally trade real
 *      money" is impossible. Disarming is always one click (the fail-safe way).
 *   3. The notification delivery-policy matrix.
 */
import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import QRCode from "qrcode";
import { useI18n } from "@richrich/i18n/react";
import type { Locale } from "@richrich/i18n";
import type { Currency, GetSettingsResponse } from "@richrich/contracts";
import { PageShell } from "../../components/PageShell";
import { ApiError } from "../../lib/api";
import { settingsQuery } from "../../lib/query";
import { useIssueQrLogin, useUpdateSettings } from "../../features/settings/hooks";
import {
  Button,
  ErrorNote,
  SectionCard,
  Select,
  SettingRow,
  TextField,
  Toggle,
} from "../../components/ui/controls";
import { NotificationPolicyMatrix } from "../../features/notifications/policy";

export const Route = createFileRoute("/settings/general")({
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(settingsQuery);
  },
  component: GeneralSettingsPage,
});

// Language is a client-only UI preference (localStorage + `<html lang>`), not a
// server setting — so the switcher lives here but does not go through the settings
// mutation. `한국어 / English` is shown bilingually so it is findable in either mode.
const LANGUAGE_OPTIONS: readonly { value: Locale; label: string }[] = [
  { value: "ko", label: "한국어" },
  { value: "en", label: "English" },
];

function GeneralSettingsPage() {
  const { t } = useI18n();
  const settings = useQuery(settingsQuery);

  return (
    <PageShell
      eyebrow={t("settings.generalEyebrow")}
      title={t("settings.generalTitle")}
      description={t("settings.generalDescription")}
      loading={settings.isLoading}
    >
      {settings.data !== undefined ? (
        <div className="flex flex-col gap-6">
          <LanguageSection />
          <DefaultsSection settings={settings.data} />
          <ArmSection settings={settings.data} />
          <MobileSignInSection />
          <SectionCard
            title={t("nav.notifications")}
            description={t("settings.notificationsDescription")}
          >
            <NotificationPolicyMatrix />
          </SectionCard>
        </div>
      ) : null}
    </PageShell>
  );
}

function LanguageSection() {
  const { t, locale, setLocale } = useI18n();
  return (
    <SectionCard
      title={t("settings.languageTitle")}
      description={t("settings.languageDescription")}
    >
      <div className="max-w-xs">
        <Select<Locale>
          label={t("settings.languageTitle")}
          value={locale}
          options={LANGUAGE_OPTIONS}
          onValueChange={setLocale}
          hint={t("settings.languageHint")}
        />
      </div>
    </SectionCard>
  );
}

function DefaultsSection({ settings }: { settings: GetSettingsResponse }) {
  const { t } = useI18n();
  const update = useUpdateSettings();
  const [timezone, setTimezone] = useState(settings.timezone);
  const [baseCurrency, setBaseCurrency] = useState<Currency>(settings.baseCurrency);
  const [defaultDryRun, setDefaultDryRun] = useState(settings.defaultDryRun);

  // Resync from the server projection when it changes and there is no pending edit.
  useEffect(() => {
    if (!update.isPending) {
      setTimezone(settings.timezone);
      setBaseCurrency(settings.baseCurrency);
      setDefaultDryRun(settings.defaultDryRun);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.timezone, settings.baseCurrency, settings.defaultDryRun]);

  const dirty =
    timezone !== settings.timezone ||
    baseCurrency !== settings.baseCurrency ||
    defaultDryRun !== settings.defaultDryRun;

  const currencyOptions: readonly { value: Currency; label: string }[] = [
    { value: "KRW" as Currency, label: t("settings.currencyKrw") },
    { value: "USD" as Currency, label: t("settings.currencyUsd") },
  ];

  return (
    <SectionCard
      title={t("settings.defaultsTitle")}
      description={t("settings.defaultsDescription")}
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label={t("settings.timezoneLabel")}
            mono
            value={timezone}
            onValueChange={setTimezone}
            placeholder="Asia/Seoul"
            hint={t("settings.timezoneHint")}
          />
          <Select
            label={t("settings.baseCurrencyLabel")}
            value={baseCurrency}
            options={currencyOptions}
            onValueChange={setBaseCurrency}
            hint={t("settings.baseCurrencyHint")}
          />
        </div>
        <SettingRow
          label={t("settings.defaultDryRunLabel")}
          description={t("settings.defaultDryRunDescription")}
          control={
            <Toggle
              checked={defaultDryRun}
              onCheckedChange={setDefaultDryRun}
              label={t("settings.defaultDryRunLabel")}
            />
          }
        />
        <div className="flex items-center gap-3 border-t border-hairline pt-4">
          <Button
            variant="primary"
            disabled={!dirty}
            pending={update.isPending}
            onClick={() => update.mutate({ timezone, baseCurrency, defaultDryRun })}
          >
            {t("settings.saveDefaults")}
          </Button>
          {update.isError ? <ErrorNote>{t("common.saveFailed")}</ErrorNote> : null}
        </div>
      </div>
    </SectionCard>
  );
}

function ArmSection({ settings }: { settings: GetSettingsResponse }) {
  const { t } = useI18n();
  const update = useUpdateSettings();
  const armed = settings.tradingArmed;
  const tossReady = settings.features.toss === true;
  const [confirmOpen, setConfirmOpen] = useState(false);

  function disarm() {
    update.mutate({ tradingArmed: false });
  }

  return (
    <SectionCard
      title={t("settings.liveTradingTitle")}
      description={t("settings.liveTradingDescription")}
      aside={
        <span
          className={
            armed
              ? "font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-critical"
              : "font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-muted"
          }
        >
          {armed ? t("settings.armedBadge") : t("settings.disarmedBadge")}
        </span>
      }
    >
      <div className="flex flex-col gap-3">
        <SettingRow
          label={t("settings.armLiveTrading")}
          description={
            armed ? t("settings.armDescriptionArmed") : t("settings.armDescriptionDisarmed")
          }
          control={
            <Toggle
              tone="critical"
              checked={armed}
              disabled={update.isPending}
              onCheckedChange={(next) => (next ? setConfirmOpen(true) : disarm())}
              label={t("settings.armLiveTrading")}
            />
          }
        />
        {!tossReady ? (
          <p className="text-xs text-serious">
            {t("settings.tossRequiredBeforeArm")}{" "}
            <Link
              to="/settings/toss"
              className="font-medium text-brand underline-offset-2 hover:underline"
            >
              {t("settings.connectTossLink")}
            </Link>
          </p>
        ) : null}
        {update.isError && !confirmOpen ? <ArmError error={update.error} /> : null}
      </div>

      {confirmOpen ? (
        <ArmConfirmDialog
          pending={update.isPending}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() =>
            update.mutate(
              { tradingArmed: true, confirm: true },
              { onSuccess: () => setConfirmOpen(false) },
            )
          }
          error={update.isError ? update.error : null}
        />
      ) : null}
    </SectionCard>
  );
}

/**
 * Mobile QR sign-in — mint a single-use, 2-minute token (`POST /api/auth/qr/issue`)
 * and render it as a `richrich://qr-login?token=…` QR. Scanning it with the phone
 * camera opens the app, which exchanges the token for its own session — no code
 * typing on the phone. The plaintext token lives only in this component's state.
 */
function MobileSignInSection() {
  const { t } = useI18n();
  const issue = useIssueQrLogin();
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [remaining, setRemaining] = useState(0);

  const issued = issue.data;

  // Render the QR whenever a fresh token arrives.
  useEffect(() => {
    if (issued === undefined) return;
    let alive = true;
    void QRCode.toDataURL(issued.deeplink, { margin: 1, width: 220 }).then((url) => {
      if (alive) setDataUrl(url);
    });
    return () => {
      alive = false;
    };
  }, [issued]);

  // Countdown to expiry (drives the expired state).
  useEffect(() => {
    if (issued === undefined) return;
    const tick = () => setRemaining(Math.max(0, Math.ceil((issued.expiresAt - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [issued]);

  const expired = issued !== undefined && remaining <= 0;

  return (
    <SectionCard
      title={t("settings.qrSignInTitle")}
      description={t("settings.qrSignInDescription")}
    >
      <div className="flex flex-col gap-3">
        {issued !== undefined && dataUrl !== null ? (
          <div className="flex items-start gap-4">
            <img
              src={dataUrl}
              alt={t("settings.qrSignInTitle")}
              width={160}
              height={160}
              className={
                expired
                  ? "rounded-md border border-hairline opacity-20"
                  : "rounded-md border border-hairline"
              }
            />
            <div className="flex flex-col gap-1 pt-1 text-sm">
              <p className="text-ink-secondary">{t("settings.qrSignInScanHint")}</p>
              <p className={expired ? "text-serious" : "font-mono text-xs text-muted"}>
                {expired
                  ? t("settings.qrSignInExpired")
                  : t("settings.qrSignInExpiresIn", { seconds: String(remaining) })}
              </p>
            </div>
          </div>
        ) : null}
        <div className="flex items-center gap-3">
          <Button
            variant={issued === undefined ? "primary" : "secondary"}
            pending={issue.isPending}
            onClick={() => issue.mutate()}
          >
            {issued === undefined
              ? t("settings.qrSignInGenerate")
              : t("settings.qrSignInRegenerate")}
          </Button>
          {issue.isError ? <ErrorNote>{t("settings.qrSignInFailed")}</ErrorNote> : null}
        </div>
      </div>
    </SectionCard>
  );
}

function ArmError({ error }: { error: unknown }) {
  const { t } = useI18n();
  if (error instanceof ApiError && error.code === "TRADING_NOT_READY") {
    return <ErrorNote>{t("settings.armErrorNotReady")}</ErrorNote>;
  }
  return <ErrorNote>{t("settings.armErrorGeneric")}</ErrorNote>;
}

function ArmConfirmDialog({
  pending,
  onCancel,
  onConfirm,
  error,
}: {
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  error: unknown;
}) {
  const { t } = useI18n();
  const [acknowledged, setAcknowledged] = useState(false);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("settings.armLiveTrading")}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div className="w-full max-w-md rounded-panel border border-critical/40 bg-surface p-6 shadow-xl">
        <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-critical">
          {t("settings.armLiveTrading")}
        </div>
        <h2 className="mt-1 text-lg font-semibold text-ink">{t("settings.confirmHeading")}</h2>
        <p className="mt-2 text-sm text-ink-secondary">{t("settings.confirmBody")}</p>
        <label className="mt-4 flex items-start gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-0.5 size-4 cursor-pointer accent-[var(--color-critical)]"
          />
          {t("settings.confirmAck")}
        </label>
        {error !== null ? (
          <div className="mt-3">
            <ArmError error={error} />
          </div>
        ) : null}
        <div className="mt-5 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={pending}>
            {t("common.cancel")}
          </Button>
          <Button variant="danger" disabled={!acknowledged} pending={pending} onClick={onConfirm}>
            {t("settings.armLiveTrading")}
          </Button>
        </div>
      </div>
    </div>
  );
}
